/**
 * ViewErrorBoundary Component
 *
 * Error boundary that catches errors in view components.
 * Displays a user-friendly error message with retry option.
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { emitNavEvent } from '../../utils/navigationUtils';

class ViewErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Emit telemetry event
    emitNavEvent('view_error', {
      view_id: this.props.viewId,
      error_message: error?.message,
      error_stack: error?.stack?.slice(0, 500), // Truncate stack
      component_stack: errorInfo?.componentStack?.slice(0, 500),
    }, this.props.user);

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('[ViewErrorBoundary] Error caught:', error);
      console.error('[ViewErrorBoundary] Component stack:', errorInfo?.componentStack);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    // Navigate to default view
    window.location.hash = '#/time/my-work';
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const { viewTitle } = this.props;
      const isDev = import.meta.env.DEV;

      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-red-600" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h2>

            {/* Description */}
            <p className="text-gray-500 text-base mb-6">
              {viewTitle
                ? `We couldn't load "${viewTitle}". Please try again.`
                : 'This view encountered an error. Please try again.'}
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-900 bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-slate-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Home className="w-4 h-4" />
                Go to My Work
              </button>
            </div>

            {/* Dev error details */}
            {isDev && this.state.error && (
              <div className="mt-8 text-left">
                <details className="bg-slate-200 rounded-lg p-4">
                  <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                    Error Details (Development Only)
                  </summary>
                  <pre className="mt-3 text-xs text-red-600 overflow-auto max-h-48 whitespace-pre-wrap">
                    {this.state.error.toString()}
                    {'\n\n'}
                    {this.state.error.stack}
                  </pre>
                  {this.state.errorInfo?.componentStack && (
                    <pre className="mt-2 text-xs text-gray-500 overflow-auto max-h-32 whitespace-pre-wrap">
                      Component Stack:
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </details>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ViewErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  viewId: PropTypes.string,
  viewTitle: PropTypes.string,
  user: PropTypes.object,
};

ViewErrorBoundary.defaultProps = {
  viewId: null,
  viewTitle: null,
  user: null,
};

export default ViewErrorBoundary;
